// One delegated gesture owner survives message reconciliation, including retained video articles.
// It opens existing menus; actions, authorization and transport remain with their original owners.
export function installMessageMenuGestures(root: HTMLElement): void {
  const selected = (): boolean => Boolean(window.getSelection()?.toString());
  const article = (target: EventTarget | null): HTMLElement | null => {
    const node = target instanceof Element ? target.closest<HTMLElement>('.message[data-message-id]') : null;
    return node && root.contains(node) && node.closest('#messages') ? node : null;
  };
  const interactive = (target: EventTarget | null): boolean => target instanceof Element
    && Boolean(target.closest('a,button,input,textarea,select,video,audio,[contenteditable],[role="button"]'));
  const triggerFor = (message: HTMLElement): HTMLButtonElement | null => message.querySelector('.message-more.menu-trigger');
  const open = (message: HTMLElement, x: number, y: number): void => {
    if (root.contains(message)) triggerFor(message)?.dispatchEvent(new CustomEvent('open-menu', { detail: { x, y } }));
  };
  let hold: { id: number; x: number; y: number; timer: ReturnType<typeof setTimeout>; listeners: AbortController } | null = null;
  let consumed: { message: HTMLElement; until: number } | null = null;
  let lastPointerType = '';
  const cancel = (): void => {
    if (!hold) return;
    clearTimeout(hold.timer); hold.listeners.abort(); hold = null;
  };
  const suppress = (event: Event): boolean => consumed !== null && Date.now() <= consumed.until
    && article(event.target) === consumed.message
    && !(event.target instanceof Element && event.target.closest('.action-menu'));
  root.addEventListener('contextmenu', event => {
    const message = article(event.target), trigger = message && triggerFor(message);
    if (!message || !trigger || (interactive(event.target) && !trigger.contains(event.target as Node))) return;
    // A right-click may select its word before contextmenu; selection must not block the menu.
    // Legacy touch context menus are MouseEvents; keep their native text-selection behavior.
    const fromTouch = 'pointerType' in event && event.pointerType ? event.pointerType === 'touch' : lastPointerType === 'touch';
    if (fromTouch && selected()) return;
    event.preventDefault();
    if (hold) consumed = { message, until: Date.now() + 60000 };
    cancel(); open(message, event.clientX, event.clientY);
  });
  root.addEventListener('pointerdown', event => {
    lastPointerType = event.pointerType;
    cancel(); consumed = null;
    const message = article(event.target);
    if (!message || !triggerFor(message) || event.pointerType !== 'touch' || !event.isPrimary || event.button !== 0 || interactive(event.target) || selected()) return;
    const listeners = new AbortController();
    const x = event.clientX, y = event.clientY, version = message.dataset.version;
    const timer = setTimeout(() => {
      cancel();
      if (selected() || !root.contains(message) || message.dataset.version !== version || !triggerFor(message)) return;
      consumed = { message, until: Date.now() + 60000 };
      open(message, x, y);
    }, 450);
    hold = { id: event.pointerId, x, y, timer, listeners };
    const options = { capture: true, signal: listeners.signal };
    document.addEventListener('pointerdown', cancel, options);
    document.addEventListener('pointermove', move => {
      if (hold && (move.pointerId !== hold.id || Math.hypot(move.clientX - hold.x, move.clientY - hold.y) > 10)) cancel();
    }, options);
    document.addEventListener('pointerup', cancel, options);
    document.addEventListener('pointercancel', cancel, options);
    document.addEventListener('scroll', cancel, options);
    document.addEventListener('selectionchange', () => { if (selected()) cancel(); }, { signal: listeners.signal });
    window.addEventListener('blur', cancel, { signal: listeners.signal });
  });
  for (const type of ['pointerup', 'pointercancel']) root.addEventListener(type, event => {
    if ((event as PointerEvent).pointerType === 'touch' && consumed) consumed.until = Date.now() + 1000;
  }, true);
  // Touch release generates a compatibility mousedown before click. Prevent its default
  // focus transfer: otherwise the existing menu's outside-focus handler closes the menu.
  root.addEventListener('mousedown', event => { if (suppress(event)) event.preventDefault(); }, true);
  root.addEventListener('click', event => {
    if (!suppress(event)) return;
    consumed = null; event.preventDefault(); event.stopPropagation();
  }, true);
}
